import { useEffect, useRef, useState } from 'react';
import { stationeryApi } from '../../api';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ExcelUploadMenu from '../../components/common/ExcelUploadMenu';
import DeleteBlockedModal from '../../components/common/DeleteBlockedModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MaterialInfoFieldsModal from '../../components/common/MaterialInfoFieldsModal';

// ── Category badge ────────────────────────────────────────────────────────────
const CategoryBadge = ({ category }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
    category === 'CONSUMABLE'
      ? 'bg-green-100 text-green-700'
      : 'bg-purple-100 text-purple-700'
  }`}>
    {category === 'CONSUMABLE' ? 'Consumable' : 'Durable'}
  </span>
);

const emptyItem = { name: '', category: 'CONSUMABLE', unit: 'pcs', price: '', validityDays: '', description: '', maxQuantityPerWorker: '1', requiresMaterialId: false };

export default function StationeryManagement() {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [items, setItems]                           = useState([]);
  const [itemMasterCategory, setItemMasterCategory] = useState('CONSUMABLE');
  const [showItemForm, setShowItemForm]             = useState(false);
  const [itemForm, setItemForm]                     = useState(emptyItem);
  const [editItemId, setEditItemId]                 = useState(null);
  // Snapshot of requiresMaterialId at the moment Edit was opened — lets the
  // checkbox's onChange detect a real transition and confirm it.
  const [origRequiresMaterialId, setOrigRequiresMaterialId]  = useState(false);
  const [deleteCheck, setDeleteCheck]               = useState(null);
  const [deleteError, setDeleteError]               = useState('');
  const fileInputRef = useRef(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult]   = useState(null);
  const [materialIdToggleConfirm, setMaterialIdToggleConfirm] = useState(null); // { next }
  const [search, setSearch] = useState('');
  const [infoModalItem, setInfoModalItem] = useState(null);

  const loadItems = async () => {
    try {
      const res = await stationeryApi.listItems();
      setItems(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load stationery items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []); // eslint-disable-line

  const handleDownloadTemplate = async () => {
    setError('');
    try {
      const res = await stationeryApi.downloadTemplate(itemMasterCategory);
      const filename = itemMasterCategory === 'DURABLE' ? 'stationery_durables_template.xlsx' : 'stationery_consumables_template.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.response?.data?.message || 'Failed to download template'); }
  };

  const handleBulkUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBulkLoading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await stationeryApi.bulkUpload(formData);
      setBulkResult(res.data.data);
      loadItems();
    } catch (err) { setError(err.response?.data?.message || 'Bulk upload failed'); }
    finally { setBulkLoading(false); }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = {
        ...itemForm,
        price: itemForm.price ? parseFloat(itemForm.price) : null,
        validityDays: itemForm.category === 'DURABLE' && itemForm.validityDays !== '' ? parseInt(itemForm.validityDays, 10) : null,
        maxQuantityPerWorker: itemForm.maxQuantityPerWorker !== '' ? parseInt(itemForm.maxQuantityPerWorker, 10) : 1,
        requiresMaterialId: !!itemForm.requiresMaterialId,
      };
      if (editItemId) {
        await stationeryApi.updateItem(editItemId, data);
      } else {
        await stationeryApi.createItem(data);
      }
      setShowItemForm(false);
      setItemForm(emptyItem);
      setEditItemId(null);
      const res = await stationeryApi.listItems();
      setItems(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving item');
    }
  };

  const toggleItemActive = async (id) => {
    try {
      await stationeryApi.toggleItemActive(id);
      const res = await stationeryApi.listItems();
      setItems(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error toggling item');
    }
  };

  const openDelete = async (it) => {
    setError(''); setDeleteError('');
    try {
      const res = await stationeryApi.checkItemDelete(it.id);
      setDeleteCheck({ item: it, ...res.data.data });
    } catch (err) { setError(err.response?.data?.message || 'Error checking item'); }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteCheck) return;
    setDeleteError('');
    try {
      await stationeryApi.deleteItem(deleteCheck.item.id);
      setDeleteCheck(null);
      const res = await stationeryApi.listItems();
      setItems(res.data.data);
    } catch (err) { setDeleteError(err.response?.data?.message || 'Error deleting item'); }
  };

  const handleDisableInstead = async () => {
    if (!deleteCheck) return;
    setDeleteError('');
    try {
      await stationeryApi.toggleItemActive(deleteCheck.item.id);
      setDeleteCheck(null);
      const res = await stationeryApi.listItems();
      setItems(res.data.data);
    } catch (err) { setDeleteError(err.response?.data?.message || 'Error disabling item'); }
  };

  const visibleItems = items
    .filter((it) => it.category === itemMasterCategory)
    .filter((it) => !search || it.name.toLowerCase().includes(search.toLowerCase()) || (it.description || '').toLowerCase().includes(search.toLowerCase()));

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="Stationery"
        subtitle="Manage stationery item definitions"
        action={
          <div className="flex gap-2">
            <ExcelUploadMenu
              loading={bulkLoading}
              onDownloadTemplate={handleDownloadTemplate}
              onUploadClick={() => fileInputRef.current?.click()}
            />
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUploadChange} />
            <button
              className="btn-primary"
              onClick={() => {
                setItemForm({ ...emptyItem, category: itemMasterCategory });
                setEditItemId(null);
                setError('');
                setShowItemForm(true);
              }}
            >
              + Add Item
            </button>
          </div>
        }
      />

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
          {error}
        </p>
      )}

      {/* Consumable / Durable toggle */}
      <div className="flex gap-3 mb-6 flex-wrap items-center justify-between">
        <div className="flex gap-1">
          {[['CONSUMABLE', 'Consumable'], ['DURABLE', 'Durable']].map(([v, l]) => (
            <button
              key={v}
              className={itemMasterCategory === v ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setItemMasterCategory(v)}
            >
              {l}
            </button>
          ))}
        </div>
        <input
          className="input max-w-xs"
          placeholder="Search stationery…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Price (₹)</th>
              {itemMasterCategory === 'DURABLE' && <th>Validity (Days)</th>}
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((it) => (
              <tr key={it.id}>
                <td className="font-medium">
                  {it.requiresMaterialId ? (
                    <button className="hover:underline text-left" onClick={() => setInfoModalItem(it)}>{it.name}</button>
                  ) : it.name}
                </td>
                <td><CategoryBadge category={it.category} /></td>
                <td className="text-gray-500">{it.unit}</td>
                <td className="font-semibold">
                  {it.price ? `₹${parseFloat(it.price).toFixed(2)}` : '—'}
                </td>
                {itemMasterCategory === 'DURABLE' && (
                  <td className="text-gray-500">{it.validityDays != null ? `${it.validityDays} days` : '—'}</td>
                )}
                <td className="text-gray-400 text-sm max-w-xs truncate">{it.description || '—'}</td>
                <td>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    it.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {it.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <button
                      className="text-blue-600 hover:underline text-sm"
                      onClick={() => {
                        setItemForm({
                          name:        it.name,
                          category:    it.category,
                          unit:        it.unit,
                          price:       it.price ?? '',
                          validityDays: it.validityDays != null ? String(it.validityDays) : '',
                          description: it.description ?? '',
                          maxQuantityPerWorker: String(it.maxQuantityPerWorker ?? 1),
                          requiresMaterialId: !!it.requiresMaterialId,
                        });
                        setEditItemId(it.id);
                        setOrigRequiresMaterialId(!!it.requiresMaterialId);
                        setError('');
                        setShowItemForm(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className={`text-sm hover:underline ${it.isActive ? 'text-orange-500' : 'text-green-600'}`}
                      onClick={() => toggleItemActive(it.id)}
                    >
                      {it.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className="text-red-500 hover:underline text-sm"
                      onClick={() => openDelete(it)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan={itemMasterCategory === 'DURABLE' ? 8 : 7} className="text-center py-10 text-gray-400">
                  No {itemMasterCategory === 'CONSUMABLE' ? 'consumable' : 'durable'} items defined yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────────── */}
      {deleteCheck && (
        <DeleteBlockedModal
          title="Delete Stationery Item"
          itemName={deleteCheck.item.name}
          safe={deleteCheck.safe}
          blockers={deleteCheck.issuances > 0 ? [{ label: 'Issuance records', count: deleteCheck.issuances, color: 'indigo' }] : []}
          resolutionAvailable={deleteCheck.issuances > 0 && deleteCheck.isActive}
          resolutionLabel="Disable Instead"
          resolutionHint="This item has issuance history and can never be deleted — disable it instead to stop it being used for new issuances."
          blockedHint="This item is already disabled and has issuance history — it can never be deleted."
          onResolve={handleDisableInstead}
          onConfirmDelete={handleDeleteConfirmed}
          onClose={() => setDeleteCheck(null)}
          error={deleteError}
        />
      )}

      {/* ── Add / Edit Item Modal ─────────────────────────────────────────────── */}
      {showItemForm && (
        <Modal
          title={`${editItemId ? 'Edit' : 'Add'} Stationery Item`}
          onClose={() => { setShowItemForm(false); setError(''); }}
        >
          <form onSubmit={handleItemSubmit} className="space-y-4">
            <div>
              <label className="label">Name</label>
              <input
                className="input"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Category</label>
                <select
                  className="input"
                  value={itemForm.category}
                  onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                  disabled={!!editItemId}
                >
                  <option value="CONSUMABLE">Consumable</option>
                  <option value="DURABLE">Durable</option>
                </select>
              </div>
              <div>
                <label className="label">Unit</label>
                <input
                  className="input"
                  value={itemForm.unit}
                  onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">
                Price (₹)
                {itemForm.category === 'DURABLE' && (
                  <span className="text-red-500 ml-1">*</span>
                )}
                {itemForm.category === 'CONSUMABLE' && (
                  <span className="text-gray-400 font-normal ml-1">(optional)</span>
                )}
              </label>
              <input
                className="input"
                type="number"
                step="0.01"
                min="0"
                value={itemForm.price}
                onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                required={itemForm.category === 'DURABLE'}
              />
            </div>

            {itemForm.category === 'DURABLE' && (
              <div>
                <label className="label">
                  Validity (Days) <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={itemForm.validityDays}
                  onChange={(e) => setItemForm({ ...itemForm, validityDays: e.target.value })}
                  placeholder="e.g. 365"
                />
              </div>
            )}

            <div>
              <label className="label">Maximum Quantity Allowed <span className="text-gray-400 font-normal">(per worker, across all sites)</span></label>
              <input
                className="input"
                type="number"
                min="1"
                value={itemForm.maxQuantityPerWorker}
                onChange={(e) => setItemForm({ ...itemForm, maxQuantityPerWorker: e.target.value })}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={itemForm.requiresMaterialId}
                onChange={(e) => {
                  const next = e.target.checked;
                  if (editItemId && next !== origRequiresMaterialId) {
                    setMaterialIdToggleConfirm({ next });
                    return;
                  }
                  setItemForm({ ...itemForm, requiresMaterialId: next });
                }}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">Requires Material ID <span className="text-gray-400">(each unit tracked individually with a unique ID)</span></span>
            </label>

            <div>
              <label className="label">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                className="input"
                rows={2}
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <ModalActions
              onCancel={() => { setShowItemForm(false); setError(''); }}
              submitLabel={editItemId ? 'Save Changes' : 'Add Item'}
            />
          </form>
        </Modal>
      )}

      {/* ── Excel Upload Results Modal ─────────────────────────────────────────── */}
      {bulkResult && (
        <Modal title="Excel Upload Results" onClose={() => setBulkResult(null)}>
          <div className="space-y-3">
            <div className="flex gap-4 text-sm flex-wrap">
              <span className="text-green-700 font-medium">{bulkResult.created} created</span>
              <span className="text-blue-700 font-medium">{bulkResult.updated || 0} updated</span>
              <span className="text-red-600 font-medium">{bulkResult.failed} failed</span>
              <span className="text-gray-400">{bulkResult.total} total row{bulkResult.total !== 1 ? 's' : ''}</span>
            </div>
            {bulkResult.failedRecords?.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Row</th>
                      <th className="text-left px-3 py-2">Identifier</th>
                      <th className="text-left px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResult.failedRecords.map((f, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-1.5">{f.row}</td>
                        <td className="px-3 py-1.5">{f.identifier}</td>
                        <td className="px-3 py-1.5 text-red-600">{f.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
            <button className="btn-primary" onClick={() => setBulkResult(null)}>Close</button>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!materialIdToggleConfirm}
        title="Change Material ID Requirement?"
        message={materialIdToggleConfirm?.next
          ? 'Turning this ON only affects future transactions — existing stock keeps its current state. Issuing or returning this item will require a Material ID from now on.'
          : 'Turning this OFF only affects future transactions — existing Material IDs are kept for history but hidden from new issue/return/transfer pickers.'}
        danger={false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={() => { setItemForm({ ...itemForm, requiresMaterialId: materialIdToggleConfirm.next }); setMaterialIdToggleConfirm(null); }}
        onCancel={() => setMaterialIdToggleConfirm(null)}
      />

      <MaterialInfoFieldsModal
        show={!!infoModalItem}
        onClose={() => setInfoModalItem(null)}
        stationeryItemId={infoModalItem?.id}
        materialName={infoModalItem?.name}
      />
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, submitLabel, disabled = false }) => (
  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className="btn-primary" disabled={disabled}>{submitLabel}</button>
  </div>
);
